import { lazy } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router";

import ProtectedAdminRoute from "./components/guards/ProtectedAdminRoute";
import ProtectedOrganizationRoute from "./components/guards/ProtectedOrganizationRoute";
import ProtectedRoute from "./components/guards/ProtectedRoute";
import ScopesEnabledRoute from "./components/guards/ScopesEnabledRoute";
import Page404 from "./pages/404";
import Carriers from "./pages/admin/carriers/Carriers";
import AdminClauseChecklists from "./pages/admin/clause-checklists/AdminClauseChecklists";
import ClauseChecklistDetails from "./pages/admin/clause-checklists/ClauseChecklistDetails";
import GenerateChecklist from "./pages/admin/clause-checklists/generate/GenerateChecklist";
import AdminClauseDetails from "./pages/admin/clauses/AdminClauseDetails";
import AdminClauses from "./pages/admin/clauses/AdminClauses";
import Datatypes from "./pages/admin/datatypes/Datatypes";
import DocumentSectionTypes from "./pages/admin/document-section-types/DocumentSectionTypes";
import AdminDocuments from "./pages/admin/documents/AdminDocuments";
import AdminFeedback from "./pages/admin/feedback/AdminFeedback";
import AdminFields from "./pages/admin/fields/AdminFields";
import AdminHome from "./pages/admin/home/AdminHome";
import AdminIndustryDetails from "./pages/admin/industries/AdminIndustryDetails";
import Industries from "./pages/admin/industries/Industries";
import AdminLayout from "./pages/admin/layout/AdminLayout";
import AdminOrganizationGroupDetails from "./pages/admin/organization-groups/AdminOrganizationGroupDetails";
import OrganizationGroups from "./pages/admin/organization-groups/OrganizationGroups";
import AdminOrganizationDetails from "./pages/admin/organizations/AdminOrganizationDetails";
import AdminOrganizations from "./pages/admin/organizations/AdminOrganizations";
import AdminPolicyTypeDetails from "./pages/admin/policy-types/AdminPolicyTypeDetails";
import PolicyTypes from "./pages/admin/policy-types/PolicyTypes";
import AdminScopes from "./pages/admin/scopes/AdminScopes";
import ScopeJobRunPage from "./pages/admin/scopes/job-run/ScopeJobRunPage";
import AdminAccountSettings from "./pages/admin/settings/AdminAccountSettings";
import AdminSecuritySettings from "./pages/admin/settings/AdminSecuritySettings";
import AdminSettings from "./pages/admin/settings/AdminSettings";
import AdminSkillDetails from "./pages/admin/skills/AdminSkillDetails";
import Skills from "./pages/admin/skills/Skills";
import AdminTopicDetails from "./pages/admin/topics/AdminTopicDetails";
import AdminTopics from "./pages/admin/topics/AdminTopics";
import AdminUsers from "./pages/admin/users/AdminUsers";
import AdminUsersLayout from "./pages/admin/users/AdminUsersLayout";
import UserDetails from "./pages/admin/users/UserDetails";
import { validatorAdminRoutes } from "./pages/admin/validators/validatorRoutes";
import CreateAccount from "./pages/auth/create-account/CreateAccount";
import ForgotPassword from "./pages/auth/forgot-password/ForgotPassword";
import AuthLayout from "./pages/auth/layout/AuthLayout";
import Login from "./pages/auth/login/Login";
import ResetPassword from "./pages/auth/reset-password/ResetPassword";
import Chat from "./pages/frontend/chat/Chat";
import ClauseChecklistDetail from "./pages/frontend/clause-checklists/ClauseChecklistDetail";
import OrganizationClauseChecklists from "./pages/frontend/clause-checklists/OrganizationClauseChecklists";
import OrganizationClauseLibrary from "./pages/frontend/clause-library/OrganizationClauseLibrary";
import ClientDetail from "./pages/frontend/clients/ClientDetail";
import OrganizationClients from "./pages/frontend/clients/OrganizationClients";
import Chart from "./pages/frontend/compare-in-charts/Chart";
import OrganizationCompareInCharts from "./pages/frontend/compare-in-charts/OrganizationCompareInCharts";
import { DocumentLibraryContent } from "./pages/frontend/documents/DocumentLibraryContent";
import { DocumentLibraryLayout } from "./pages/frontend/documents/DocumentLibraryLayout";
import OrganizationHome from "./pages/frontend/home/OrganizationHome";
import FrontEndLayout from "./pages/frontend/layout/FrontEndLayout";
import Scope from "./pages/frontend/scope/Scope";
import FrontEndAccountSettings from "./pages/frontend/settings/FrontEndAccountSettings";
import FrontEndSecuritySettings from "./pages/frontend/settings/FrontEndSecuritySettings";
import FrontEndSettings from "./pages/frontend/settings/FrontEndSettings";

const AdminAgentDetails = lazy(
    () => import("./pages/admin/agents/AdminAgentDetails")
);
const Agents = lazy(() => import("./pages/admin/agents/Agents"));

export const AppRoutes = () => {
    return (
        <Routes>
            <Route path="*" element={<Page404 />} />
            {/* Auth Routes */}
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/password/forgot" element={<ForgotPassword />} />
                <Route path="/password/reset" element={<ResetPassword />} />
                <Route
                    path="/create-account/invitation/:invitationCode"
                    element={<CreateAccount />}
                />
            </Route>

            {/* Session Routes */}
            <Route path="/" element={<ProtectedRoute />}>
                {/* Organization Routes */}
                <Route
                    path="/organizations"
                    element={<ProtectedOrganizationRoute />}
                >
                    <Route element={<FrontEndLayout />}>
                        <Route index element={<Navigate to="home" />} />
                        <Route path="home" element={<OrganizationHome />} />
                        <Route element={<ScopesEnabledRoute />}>
                            <Route
                                path="scopes"
                                element={
                                    <Navigate
                                        to="/organizations/document-library/documents?type_filter=scopes"
                                        replace
                                    />
                                }
                            />
                            <Route path="scopes/:scopeId" element={<Scope />} />

                            <Route
                                path="clause-library"
                                element={<OrganizationClauseLibrary />}
                            />
                            <Route
                                path="checklists"
                                element={<OrganizationClauseChecklists />}
                            />
                            <Route
                                path="checklists/:checklistId"
                                element={<ClauseChecklistDetail />}
                            />
                            <Route
                                path="comparisons"
                                element={<OrganizationCompareInCharts />}
                            >
                                <Route
                                    index
                                    element={
                                        <Navigate
                                            to="/organizations/document-library/documents?type_filter=comparisons"
                                            replace
                                        />
                                    }
                                />
                                <Route path=":chartId" element={<Chart />} />
                            </Route>
                        </Route>
                        {/* Document Library: nested routes */}
                        <Route
                            path="document-library"
                            element={<DocumentLibraryLayout />}
                        >
                            <Route
                                index
                                element={<Navigate to="documents" replace />}
                            />
                            <Route
                                path="documents"
                                element={<DocumentLibraryContent />}
                            />
                            <Route
                                path="documents/folder/:folderId"
                                element={<DocumentLibraryContent />}
                            />
                            <Route
                                path="knowledge-base"
                                element={<DocumentLibraryContent />}
                            />
                            <Route
                                path="knowledge-base/folder/:folderId"
                                element={<DocumentLibraryContent />}
                            />
                            <Route
                                path="templates"
                                element={<DocumentLibraryContent />}
                            />
                        </Route>
                        {/* Old URL redirect */}
                        <Route
                            path="documents"
                            element={
                                <Navigate
                                    to="/organizations/document-library/documents"
                                    replace
                                />
                            }
                        />
                        <Route
                            path="clients"
                            element={<OrganizationClients />}
                        />
                        {/* Client workspace: path-based tab + folder, mirroring
                            the document library's documents/folder/:folderId
                            grammar. One ClientDetail renders at each leaf
                            (React reconciles across siblings, so the header
                            never re-skeletons); ?tab=/?folder= are retired. */}
                        <Route path="clients/:clientId">
                            <Route
                                index
                                element={<Navigate to="library" replace />}
                            />
                            <Route path="library" element={<ClientDetail />} />
                            <Route
                                path="library/folder/:folderId"
                                element={<ClientDetail />}
                            />
                            <Route
                                path="conversations"
                                element={<ClientDetail />}
                            />
                        </Route>

                        <Route path="chat">
                            <Route index element={<Chat />} />
                            <Route path="history" element={<Chat />} />
                            <Route path="history/shared" element={<Chat />} />
                            <Route path=":chatId" element={<Chat />} />
                        </Route>

                        {/* Frontend Settings Routes */}
                        <Route path="settings" element={<FrontEndSettings />}>
                            <Route
                                index
                                element={<Navigate to="account" replace />}
                            />
                            <Route
                                path="account"
                                element={<FrontEndAccountSettings />}
                            />
                            <Route
                                path="security"
                                element={<FrontEndSecuritySettings />}
                            />
                        </Route>
                    </Route>
                </Route>

                {/* Admin Routes */}
                <Route path="/admin" element={<ProtectedAdminRoute />}>
                    <Route element={<AdminLayout />}>
                        <Route index element={<Navigate to="home" />} />
                        <Route path="home" element={<AdminHome />} />
                        <Route path="scopes" element={<Outlet />}>
                            <Route index element={<AdminScopes />} />
                            <Route
                                path=":scopeId/job-run"
                                element={<ScopeJobRunPage />}
                            />
                        </Route>
                        <Route path="users" element={<AdminUsersLayout />}>
                            <Route index element={<AdminUsers />} />
                            <Route
                                path=":userId/details"
                                element={<UserDetails />}
                            />
                        </Route>
                        <Route path="clauses" element={<Outlet />}>
                            <Route index element={<AdminClauses />} />
                            <Route
                                path=":clauseId/details"
                                element={<AdminClauseDetails />}
                            />
                        </Route>
                        <Route path="documents" element={<AdminDocuments />} />
                        <Route path="topics" element={<Outlet />}>
                            <Route index element={<AdminTopics />} />
                            <Route
                                path=":topicId/details"
                                element={<AdminTopicDetails />}
                            />
                        </Route>
                        <Route path="datatypes" element={<Datatypes />} />
                        <Route path="fields" element={<AdminFields />} />
                        <Route path="policy-types" element={<Outlet />}>
                            <Route index element={<PolicyTypes />} />
                            <Route
                                path=":policyTypeId/details"
                                element={<AdminPolicyTypeDetails />}
                            />
                        </Route>
                        <Route path="industries" element={<Outlet />}>
                            <Route index element={<Industries />} />
                            <Route
                                path=":industryId/details"
                                element={<AdminIndustryDetails />}
                            />
                        </Route>
                        <Route path="checklists" element={<Outlet />}>
                            <Route index element={<AdminClauseChecklists />} />
                            <Route
                                path="generate"
                                element={<GenerateChecklist />}
                            />
                            <Route
                                path=":clauseChecklistId/details"
                                element={<ClauseChecklistDetails />}
                            />
                        </Route>
                        <Route
                            path="document-section-types"
                            element={<DocumentSectionTypes />}
                        />
                        <Route path="feedback" element={<AdminFeedback />} />

                        <Route path="organizations" element={<Outlet />}>
                            <Route index element={<AdminOrganizations />} />
                            <Route
                                path=":orgId/details"
                                element={<AdminOrganizationDetails />}
                            />
                        </Route>

                        <Route path="organization-groups" element={<Outlet />}>
                            <Route index element={<OrganizationGroups />} />
                            <Route
                                path=":groupId/details"
                                element={<AdminOrganizationGroupDetails />}
                            />
                        </Route>

                        <Route path="carriers" element={<Carriers />} />

                        <Route path="agents" element={<Outlet />}>
                            <Route index element={<Agents />} />
                            <Route
                                path=":agentId/details"
                                element={<AdminAgentDetails />}
                            />
                        </Route>

                        <Route path="skills" element={<Outlet />}>
                            <Route index element={<Skills />} />
                            <Route
                                path=":skillId/details"
                                element={<AdminSkillDetails />}
                            />
                        </Route>

                        {validatorAdminRoutes}

                        {/* Admin Settings Routes */}
                        <Route path="settings" element={<AdminSettings />}>
                            <Route
                                index
                                element={<Navigate to="account" replace />}
                            />
                            <Route
                                path="account"
                                element={<AdminAccountSettings />}
                            />
                            <Route
                                path="security"
                                element={<AdminSecuritySettings />}
                            />
                        </Route>
                    </Route>
                </Route>
            </Route>
        </Routes>
    );
};
